import React, { useState, useEffect, useCallback } from "react";
import { useUser } from "@clerk/clerk-react";
import {
  Sparkles,
  MessageCircle,
  Zap,
  TrendingUp,
  Gift,
  ChevronRight,
  ChevronLeft,
  X,
  Check,
  Play,
  SkipForward,
} from "lucide-react";
import { authFetch, apiUrl } from "../../utils/api";
import "./OnboardingFlow.css";

/**
 * OnboardingFlow - First-run experience for new users
 *
 * Features:
 * - 5 beautiful animated steps
 * - Skippable at any point
 * - Revisitable from Settings
 * - Stores completion in localStorage + syncs to backend
 * - Mobile-friendly
 * - Confetti celebration on completion
 */

const ONBOARDING_STEPS = [
  {
    id: "welcome",
    icon: Sparkles,
    title: "Welcome to Optimus",
    subtitle: "Your AI-powered business operating system",
    content: (
      <>
        <p className="onboarding-lead">
          You just unlocked a system that handles research, outreach, data entry,
          and a hundred other tasks — while you focus on what matters.
        </p>
      </>
    ),
    cta: "Show Me Around",
  },
  {
    id: "ollie",
    icon: MessageCircle,
    title: "Meet Ollie",
    subtitle: "Your conversational AI assistant",
    content: (
      <>
        <p className="onboarding-lead">
          Ollie is your go-to for quick tasks and questions. Just chat naturally:
        </p>
        <ul className="onboarding-feature-list hf-stagger">
          <li>
            <span className="feature-emoji">💬</span>
            <span>"Summarize this document for me"</span>
          </li>
          <li>
            <span className="feature-emoji">🔍</span>
            <span>"Find me 20 SaaS companies in Austin"</span>
          </li>
          <li>
            <span className="feature-emoji">✍️</span>
            <span>"Write a follow-up email to this prospect"</span>
          </li>
        </ul>
        <p className="onboarding-tip">
          <strong>Ollie = conversation.</strong> Ask questions, get answers, complete quick tasks.
        </p>
      </>
    ),
    cta: "Meet Harry →",
  },
  {
    id: "harry",
    icon: Zap,
    title: "Meet Harry",
    subtitle: "Your automation beast — works while you sleep",
    content: (
      <>
        <p className="onboarding-lead">
          Harry handles the heavy lifting. Bulk operations, scheduled tasks, background work.
        </p>
        <div className="onboarding-automation-examples harry-examples hf-stagger">
          <div className="automation-card">
            <span className="automation-icon">🦡</span>
            <div>
              <strong>"Harry, enrich these 500 leads"</strong>
              <p>Goes through each one, researches, updates your CRM</p>
            </div>
          </div>
          <div className="automation-card">
            <span className="automation-icon">⏰</span>
            <div>
              <strong>"Every Monday at 9am..."</strong>
              <p>Pull news, summarize it, post to Slack — forever</p>
            </div>
          </div>
        </div>
        <p className="onboarding-subtle">
          <strong>Harry = automation.</strong> Set it and forget it.
        </p>
      </>
    ),
    cta: "Add Connections →",
  },
  {
    id: "integrations",
    icon: Gift,
    title: "Add Connections",
    subtitle: "Connect your tools, 10x your capabilities",
    content: (
      <>
        <p className="onboarding-lead">
          Optimus gets powerful when you connect your stack:
        </p>
        <div className="onboarding-integrations hf-stagger">
          <div className="integration-pill">
            <span>📞</span> GoHighLevel
          </div>
          <div className="integration-pill">
            <span>🚀</span> Apollo
          </div>
          <div className="integration-pill">
            <span>📊</span> Airtable
          </div>
          <div className="integration-pill">
            <span>📝</span> Notion
          </div>
          <div className="integration-pill">
            <span>⚡</span> Zapier
          </div>
          <div className="integration-pill">
            <span>+</span> More
          </div>
        </div>
        <p className="onboarding-tip">
          <strong>Head to Settings → Integrations</strong> to connect your first tool.
        </p>
      </>
    ),
    cta: "Almost Done →",
  },
  {
    id: "ready",
    icon: TrendingUp,
    title: "You're Ready",
    subtitle: "Track your ROI and share the love",
    content: (
      <>
        <div className="onboarding-roi-preview compact hf-stagger">
          <div className="roi-stat">
            <span className="roi-value">📈</span>
            <span className="roi-label">ROI Dashboard</span>
          </div>
          <div className="roi-stat">
            <span className="roi-value">⚡</span>
            <span className="roi-label">Automations</span>
          </div>
          <div className="roi-stat">
            <span className="roi-value">🎁</span>
            <span className="roi-label">Affiliate</span>
          </div>
        </div>
        <div className="onboarding-highlight-box free-hook final">
          <span className="highlight-icon">🔥</span>
          <div>
            <strong>4 referrals and Optimus starts paying YOU</strong>
            <p>
              30% of every referral's payment, every month, straight to your
              bank. Your link is in the sidebar under "Affiliate."
            </p>
          </div>
        </div>
      </>
    ),
    cta: "Let's Go!",
    isFinal: true,
  },
];

// Confetti burst effect
const createConfetti = () => {
  const colors = [
    "var(--accent-primary)",
    "#7c3aed",
    "var(--accent-success)",
    "var(--accent-warning)",
    "var(--accent-danger)",
  ];
  const container = document.createElement("div");
  container.className = "confetti-container";
  document.body.appendChild(container);

  for (let i = 0; i < 100; i++) {
    const confetti = document.createElement("div");
    confetti.className = "confetti";
    confetti.style.left = Math.random() * 100 + "%";
    confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
    confetti.style.animationDelay = Math.random() * 0.5 + "s";
    confetti.style.animationDuration = (Math.random() * 2 + 2) + "s";
    container.appendChild(confetti);
  }

  setTimeout(() => container.remove(), 4000);
};

const OnboardingFlow = ({ onComplete, onSkip, isReplay = false }) => {
  const { user } = useUser();
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState("forward");
  const [isExiting, setIsExiting] = useState(false);

  const step = ONBOARDING_STEPS[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === ONBOARDING_STEPS.length - 1;
  const progress = ((currentStep + 1) / ONBOARDING_STEPS.length) * 100;

  const handleNext = useCallback(() => {
    if (isLast) {
      // Final step - complete onboarding
      createConfetti();
      setIsExiting(true);

      // Save completion
      localStorage.setItem("optimus_onboarding_complete", "true");
      localStorage.setItem("optimus_onboarding_completed_at", new Date().toISOString());

      // Sync to backend (non-blocking)
      authFetch(apiUrl("/api/settings/onboarding"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ completed: true }),
      }).catch(() => {}); // Ignore errors

      setTimeout(() => onComplete?.(), 800);
    } else {
      setDirection("forward");
      setCurrentStep((prev) => prev + 1);
    }
  }, [isLast, onComplete]);

  const handleBack = useCallback(() => {
    if (!isFirst) {
      setDirection("backward");
      setCurrentStep((prev) => prev - 1);
    }
  }, [isFirst]);

  const handleSkip = useCallback(() => {
    setIsExiting(true);
    localStorage.setItem("optimus_onboarding_skipped", "true");
    setTimeout(() => onSkip?.(), 300);
  }, [onSkip]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "ArrowRight" || e.key === "Enter") handleNext();
    if (e.key === "ArrowLeft") handleBack();
    if (e.key === "Escape") handleSkip();
  }, [handleNext, handleBack, handleSkip]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const Icon = step.icon;

  return (
    <div className={`onboarding-overlay ${isExiting ? "exiting" : ""}`}>
      <div className={`onboarding-modal ${direction}`}>
        {/* Skip button */}
        <button className="onboarding-skip" onClick={handleSkip} title="Skip tutorial">
          {isReplay ? <X size={20} /> : <SkipForward size={18} />}
          <span>{isReplay ? "Close" : "Skip"}</span>
        </button>

        {/* Progress bar */}
        <div className="onboarding-progress">
          <div className="progress-bar" style={{ width: `${progress}%` }} />
        </div>

        {/* Step indicator */}
        <div className="onboarding-steps-indicator">
          {ONBOARDING_STEPS.map((s, i) => (
            <div
              key={s.id}
              className={`step-dot ${i === currentStep ? "active" : ""} ${i < currentStep ? "completed" : ""}`}
              onClick={() => {
                setDirection(i > currentStep ? "forward" : "backward");
                setCurrentStep(i);
              }}
            >
              {i < currentStep && <Check size={10} />}
            </div>
          ))}
        </div>

        {/* Content */}
        <div key={currentStep} className={`onboarding-content step-${step.id} hf-reveal`}>
          <div className="onboarding-icon-wrapper">
            <div className="onboarding-icon-bg" />
            <Icon size={48} className="onboarding-icon" />
          </div>

          <h1 className="onboarding-title">{step.title}</h1>
          <p className="onboarding-subtitle">{step.subtitle}</p>

          <div className="onboarding-body">{step.content}</div>
        </div>

        {/* Navigation */}
        <div className="onboarding-nav">
          <button
            className="onboarding-btn secondary hf-lift hf-press hf-focus"
            onClick={handleBack}
            disabled={isFirst}
          >
            <ChevronLeft size={18} />
            Back
          </button>

          <button className="onboarding-btn primary hf-lift hf-press hf-focus" onClick={handleNext}>
            {step.isFinal ? (
              <>
                <Play size={18} />
                {step.cta}
              </>
            ) : (
              <>
                {step.cta}
                <ChevronRight size={18} />
              </>
            )}
          </button>
        </div>

        {/* Keyboard hints */}
        <div className="onboarding-keyboard-hints">
          <span>←→ Navigate</span>
          <span>Enter Continue</span>
          <span>Esc Skip</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Hook to check if onboarding should be shown
 */
export const useOnboarding = () => {
  const [shouldShow, setShouldShow] = useState(false);
  const [isReplay, setIsReplay] = useState(false);

  // Cross-device wire-up: previously we only read localStorage, so anyone
  // who cleared cookies or signed in on a second device would replay the
  // whole flow. Now we ALSO check the backend (POST /api/settings/onboarding
  // writes it; GET reads it) and cache the result back to localStorage on
  // hit so subsequent same-device loads stay fast.
  useEffect(() => {
    const skipped = localStorage.getItem("optimus_onboarding_skipped");
    if (skipped) return; // explicit skip is always honored, don't override

    const completed = localStorage.getItem("optimus_onboarding_complete");
    if (completed) return; // local completion is trusted

    // Nothing in localStorage — ask the backend before showing.
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(apiUrl("/api/settings/onboarding"));
        if (!res.ok) throw new Error("bad status");
        const data = await res.json();
        if (cancelled) return;
        if (data.onboardingCompleted) {
          // Cache locally so future loads on this device skip the fetch.
          localStorage.setItem("optimus_onboarding_complete", "true");
          if (data.completedAt) {
            localStorage.setItem("optimus_onboarding_completed_at", data.completedAt);
          }
        } else {
          setShouldShow(true);
        }
      } catch (_) {
        // Network failure / unauth — default to showing (safer than
        // silently skipping onboarding for a brand-new user).
        if (!cancelled) setShouldShow(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const showOnboarding = useCallback((replay = false) => {
    setIsReplay(replay);
    setShouldShow(true);
  }, []);

  const hideOnboarding = useCallback(() => {
    setShouldShow(false);
    setIsReplay(false);
  }, []);

  const resetOnboarding = useCallback(() => {
    localStorage.removeItem("optimus_onboarding_complete");
    localStorage.removeItem("optimus_onboarding_skipped");
    localStorage.removeItem("optimus_onboarding_completed_at");
  }, []);

  return {
    shouldShow,
    isReplay,
    showOnboarding,
    hideOnboarding,
    resetOnboarding,
  };
};

export default OnboardingFlow;
